import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { MessService } from '../mess.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ToastmessageService } from 'src/app/toastmessage.service';
import { InventoryItemContext, SupplyBuildingDialogData } from './supply-building-dialog.types';

@Component({
  selector: 'app-supply-building-dialog',
  templateUrl: './supply-building-dialog.component.html',
  styleUrls: ['./supply-building-dialog.component.scss']
})
export class SupplyBuildingDialogComponent implements OnInit {
  buildingID: any;
  currentDate: any;
  BuildingDetails: any[] = [];
  /** When true, show item image + meal qty editor instead of supply-building table. */
  isItemMode = false;
  /** Editable copy of inventory row in item mode. */
  itemEdit: any = null;
  inventoryItemContext: InventoryItemContext | null = null;
  daysheetId: number | null = null;
  canRemoveItem = false;
  private inventoryScenarios: any[] = [];
  /** Meal qty snapshot from mess-inventory grid row (source of truth for inputs). */
  private parentMealQty = {
    bfQty: 0,
    lunchQty: 0,
    snacksQty: 0,
    DinnerQty: 0,
  };

  constructor(
    private dialogRef: MatDialogRef<SupplyBuildingDialogComponent>,
    private service: MessService,
    private spinner: NgxSpinnerService,
    private toast: ToastmessageService,
    private cdr: ChangeDetectorRef,
    @Inject(MAT_DIALOG_DATA) public data: SupplyBuildingDialogData,
  ) {
    console.log('supply building dialog data', this.data);
  }

  ngOnInit(): void {
    this.isItemMode = this.data?.mode === 'item';
    if (this.isItemMode) {
      const r = this.data?.row;
      this.captureParentMealQty(r);
      this.itemEdit = r
        ? {
            ...r,
            bfQty: this.parentMealQty.bfQty,
            lunchQty: this.parentMealQty.lunchQty,
            snacksQty: this.parentMealQty.snacksQty,
            DinnerQty: this.parentMealQty.DinnerQty,
          }
        : null;
      this.inventoryItemContext = this.data?.inventoryItemContext ?? null;
      const parsedDaysheetId = Number(this.data?.daysheetId ?? this.inventoryItemContext?.daysheetId);
      this.daysheetId = Number.isFinite(parsedDaysheetId) ? parsedDaysheetId : null;
      this.loadItemFromInventory();
      return;
    }

    this.buildingID = this.data?.buildingId;
    this.currentDate = this.data?.currentDate;
    this.getSupplyBuilding();
  }

  private captureParentMealQty(row: any): void {
    this.parentMealQty = {
      bfQty: Number(row?.bfQty ?? 0),
      lunchQty: Number(row?.lunchQty ?? 0),
      snacksQty: Number(row?.snacksQty ?? 0),
      DinnerQty: Number(row?.DinnerQty ?? row?.dinnerQty ?? 0),
    };
  }

  /** Keep dialog inputs aligned with the mess-inventory grid row. */
  private bindParentMealQtyToItemEdit(): void {
    if (!this.itemEdit) {
      return;
    }
    this.itemEdit.bfQty = this.parentMealQty.bfQty;
    this.itemEdit.lunchQty = this.parentMealQty.lunchQty;
    this.itemEdit.snacksQty = this.parentMealQty.snacksQty;
    this.itemEdit.DinnerQty = this.parentMealQty.DinnerQty;
  }

  private mergeMealQtyFromInventoryScenarios(scenarios: any[], itemId: number): any | null {
    let merged: any = null;
    for (const scenario of scenarios) {
      const details = scenario?.saveInventoryBalanceDetails ?? scenario?.SaveInventoryBalanceDetails ?? [];
      const detail = (Array.isArray(details) ? details : []).find(
        (d: any) => Number(d?.itemId) === itemId,
      );
      if (!detail) {
        continue;
      }
      if (!this.inventoryItemContext) {
        this.inventoryItemContext = {
          scenarioId: Number(scenario.scenarioId),
          scenarioName: String(scenario.scenarioName ?? '').trim(),
          daysheetId: this.daysheetId ?? undefined,
          itemStatus: String(detail.itemStatus ?? '1'),
          detail,
        };
      }
      if (!merged) {
        merged = {
          bfQty: Number(detail.bfQty ?? 0),
          lunchQty: Number(detail.lunchQty ?? 0),
          snacksQty: Number(detail.snacksQty ?? 0),
          dinnerQty: Number(detail.dinnerQty ?? detail.DinnerQty ?? 0),
          ratePerUnit: Number(detail.ratePerUnit ?? 0),
          onHandQty: Number(detail.onHandQty ?? 0),
          itemStatus: String(detail.itemStatus ?? '1'),
        };
        continue;
      }
      if (Number(detail.bfQty ?? 0) > 0) {
        merged.bfQty = Number(detail.bfQty);
      }
      if (Number(detail.lunchQty ?? 0) > 0) {
        merged.lunchQty = Number(detail.lunchQty);
      }
      if (Number(detail.snacksQty ?? 0) > 0) {
        merged.snacksQty = Number(detail.snacksQty);
      }
      const dinnerQty = Number(detail.dinnerQty ?? detail.DinnerQty ?? 0);
      if (dinnerQty > 0) {
        merged.dinnerQty = dinnerQty;
      }
      merged.ratePerUnit = Number(detail.ratePerUnit ?? merged.ratePerUnit ?? 0);
      merged.onHandQty = Number(detail.onHandQty ?? merged.onHandQty ?? 0);
      merged.itemStatus = String(detail.itemStatus ?? merged.itemStatus ?? '1');
    }
    return merged;
  }

  private loadItemFromInventory(): void {
    const buildingId = this.data?.buildingId;
    const inventoryDate = String(this.data?.currentDate ?? '').trim();
    if (!buildingId || !inventoryDate) {
      this.bindParentMealQtyToItemEdit();
      this.updateRemoveState();
      this.cdr.detectChanges();
      return;
    }
    this.spinner.show();
    this.service.InventoryDetails(buildingId, inventoryDate).subscribe({
      next: (res: any) => {
        if (!res) {
          this.bindParentMealQtyToItemEdit();
          this.updateRemoveState();
          this.spinner.hide();
          this.cdr.detectChanges();
          return;
        }
        const nextDaysheetId = Number(res.daysheetId);
        if (Number.isFinite(nextDaysheetId)) {
          this.daysheetId = nextDaysheetId;
        }
        const itemId = Number(this.itemEdit?.itemId);
        const scenarios = res?.ScenarioBasedMenuItemsList ?? res?.scenarioBasedMenuItemsList ?? [];
        this.inventoryScenarios = Array.isArray(scenarios) ? scenarios : [];
        if (Array.isArray(scenarios) && Number.isFinite(itemId)) {
          const merged = this.mergeMealQtyFromInventoryScenarios(scenarios, itemId);
          if (merged) {
            this.itemEdit = {
              ...this.itemEdit,
              ratePerUnit: Number(merged.ratePerUnit ?? this.itemEdit.ratePerUnit ?? 0),
              onHandQty: Number(merged.onHandQty ?? this.itemEdit.onHandQty ?? this.itemEdit.openBalance ?? 0),
              itemStatus: String(merged.itemStatus ?? this.itemEdit.itemStatus ?? '1'),
            };
          }
        }
        this.bindParentMealQtyToItemEdit();
        this.updateRemoveState();
        this.spinner.hide();
        this.cdr.detectChanges();
      },
      error: () => {
        this.bindParentMealQtyToItemEdit();
        this.updateRemoveState();
        this.spinner.hide();
        this.cdr.detectChanges();
      },
    });
  }

  private updateRemoveState(): void {
    this.canRemoveItem = String(this.inventoryItemContext?.itemStatus ?? this.itemEdit?.itemStatus ?? '1') === '1';
  }

  getSupplyBuilding(): void {
    if (!this.buildingID) {
      this.spinner.hide();
      this.cdr.detectChanges();
      return;
    }
    this.spinner.show();
    this.service.supplyBuildingDetails(this.buildingID, this.currentDate).subscribe({
      next: (res: any) => {
        if (!res || res == 204) {
          this.BuildingDetails = [];
          this.spinner.hide();
          this.cdr.detectChanges();
          return;
        }
        this.BuildingDetails = (Array.isArray(res) ? res : (res?.data ?? [])).map((r: any) => ({
          ...r,
          studentCount: Number(r.studentCount ?? 0),
          employeeCount: Number(r.employeeCount ?? 0),
          subStaffCount: Number(r.subStaffCount ?? 0),
          visitorsCount: Number(r.visitorsCount ?? 0),
        }));
        this.spinner.hide();
        this.cdr.detectChanges();
      },
      error: () => {
        this.spinner.hide();
        this.cdr.detectChanges();
      },
    });
  }

  get itemDisplayName(): string {
    const r = this.itemEdit;
    if (!r) return '';
    return this.data?.itemNameDisplayLang === 'te'
      ? String(r.teluguItemName ?? r.itemName ?? '')
      : String(r.itemName ?? '');
  }

  get itemImageUrl(): string | null {
    const id = this.itemEdit?.itemFusionId;
    if (!id) return null;
    return `http://mess-images.s3.ap-south-1.amazonaws.com/medium/${id}.jpg`;
  }

  inrGroupedNumber(value: any): string {
    if (value === null || value === undefined || value === '') return '';
    const n = Number(String(value).replace(/,/g, ''));
    if (isNaN(n)) return '';
    return new Intl.NumberFormat('en-IN', {
      maximumFractionDigits: 2,
      minimumFractionDigits: 0,
    }).format(n);
  }

  close(): void {
    this.dialogRef.close();
  }

  submitSupplyBuildings(): void {
    this.dialogRef.close({ supplyBuildings: this.BuildingDetails });
  }

  submitItemQty(): void {
    if (!this.itemEdit) {
      this.dialogRef.close();
      return;
    }
    const payload = this.buildItemSavePayload('1', '');
    if (!payload) {
      this.toast.showError('Unable to save item. Please enter meal quantity and try again.');
      return;
    }
    this.spinner.show();
    this.service.saveItemsDetails(payload).subscribe({
      next: () => {
        this.data.onInventoryUpdated?.();
        this.spinner.hide();
        this.dialogRef.close({
          mode: 'item',
          saved: true,
          itemId: this.itemEdit.itemId,
          bfQty: Number(this.itemEdit.bfQty ?? 0),
          lunchQty: Number(this.itemEdit.lunchQty ?? 0),
          snacksQty: Number(this.itemEdit.snacksQty ?? 0),
          DinnerQty: Number(this.itemEdit.DinnerQty ?? 0),
        });
      },
      error: () => {
        this.spinner.hide();
        this.toast.showError('Failed to save item quantities.');
      },
    });
  }

  private resolveScenarioForItemSave(): { scenarioId: number; scenarioName: string } | null {
    if (this.inventoryItemContext) {
      return {
        scenarioId: this.inventoryItemContext.scenarioId,
        scenarioName: this.inventoryItemContext.scenarioName,
      };
    }
    const qtyByField: { field: 'bfQty' | 'lunchQty' | 'snacksQty' | 'DinnerQty'; keys: string[] }[] = [
      { field: 'bfQty', keys: ['breakfast'] },
      { field: 'lunchQty', keys: ['lunch'] },
      { field: 'snacksQty', keys: ['snack'] },
      { field: 'DinnerQty', keys: ['dinner'] },
    ];
    for (const { field, keys } of qtyByField) {
      if (Number(this.itemEdit?.[field] ?? 0) <= 0) {
        continue;
      }
      const scenario = this.inventoryScenarios.find((s) => {
        const name = String(s?.scenarioName ?? '').trim().toLowerCase();
        return keys.some((key) => name.includes(key));
      });
      if (scenario) {
        return {
          scenarioId: Number(scenario.scenarioId),
          scenarioName: String(scenario.scenarioName ?? '').trim(),
        };
      }
    }
    const first = this.inventoryScenarios[0];
    if (first) {
      return {
        scenarioId: Number(first.scenarioId),
        scenarioName: String(first.scenarioName ?? '').trim(),
      };
    }
    return null;
  }

  private buildItemSavePayload(status: string, menuType: string): Record<string, unknown> | null {
    const scenario = this.resolveScenarioForItemSave();
    if (!scenario || !Number.isFinite(Number(this.itemEdit?.itemId))) {
      return null;
    }
    return {
      itemType: '',
      itemId: Number(this.itemEdit.itemId),
      status,
      scenarioId: scenario.scenarioId,
      daysheetId: this.daysheetId,
      menuType,
      bfQty: Number(this.itemEdit.bfQty ?? 0),
      lunchQty: Number(this.itemEdit.lunchQty ?? 0),
      snacksQty: Number(this.itemEdit.snacksQty ?? 0),
      dinnerQty: Number(this.itemEdit.DinnerQty ?? 0),
      onHandQty: Number(this.itemEdit.onHandQty ?? this.itemEdit.openBalance ?? 0),
      inventoryDate: String(this.data?.currentDate ?? '').trim(),
      ratePerUnit: Number(this.itemEdit.ratePerUnit ?? 0),
      buildingId: this.data?.buildingId,
    };
  }

  removeItem(): void {
    if (!this.itemEdit || !this.canRemoveItem || !this.inventoryItemContext) {
      return;
    }
    const ctx = this.inventoryItemContext;
    const payload = this.buildItemSavePayload('0', ctx.scenarioName);
    if (!payload) {
      this.toast.showError('Unable to remove item.');
      return;
    }
    this.spinner.show();
    this.service.saveItemsDetails(payload).subscribe({
      next: () => {
        this.canRemoveItem = false;
        this.itemEdit.itemStatus = '0';
        this.inventoryItemContext = {
          ...ctx,
          itemStatus: '0',
        };
        this.data.onInventoryUpdated?.();
        this.spinner.hide();
        this.cdr.detectChanges();
        this.dialogRef.close({ mode: 'item', removed: true, itemId: this.itemEdit.itemId });
      },
      error: () => {
        this.spinner.hide();
        this.toast.showError('Failed to remove item.');
      },
    });
  }
}
